// src/app/routes/router.jsx
import {
    useLocation,
    useRoutes,
} from '@solidjs/router'
import {
    createEffect,
    Show,
} from 'solid-js'
import { usePublicTemplate } from '../components/templates/public-template/public-template-component-context.js'
import * as routeFiles from './web/web-routing.js'
import PartnerRouteWrapper from './partner-route-wrapper.jsx'
import {
    app,
    web,
} from '../../../.configs/app/environment.js'
import {
    SUPPORTED_LOCALES,
    DEFAULT_LOCALE,
} from '../data/static/locale-config.js'
import { useIntl } from '@cookbook/solid-intl'
import { getLocaleFromPathname } from '../utils/locale-path.js'
import { setPageMetadata } from '../utils/page-metadata.js'
import { messages as sharedMessages } from '../messages/shared.messages.en.js'
import { usePartner } from '../contexts/partner-context.jsx'

const setCanonicalLink = (url) => {
    const existingCanonical = document.querySelector('link[rel="canonical"]')
    if (existingCanonical) {
        existingCanonical.remove()
    }

    const canonicalLink = document.createElement('link')
    canonicalLink.rel = 'canonical'
    canonicalLink.href = url

    document.head.append(canonicalLink)
}

const routes = Object.values(routeFiles).filter(route => route !== null)

// Locale prefixes: all supported locales except default (default uses no URL prefix)
const localePrefixes = [...SUPPORTED_LOCALES].filter(loc => loc !== DEFAULT_LOCALE)

// Create locale-aware routes: duplicate each route for default (no prefix) and each locale prefix (e.g. /fr/, /ko-kr/)
const localeAwareRoutes = []
for (const route of routes) {
    // Original route (default locale, no prefix)
    localeAwareRoutes.push(route)

    // Routes with locale prefix (e.g. /fr, /ko-kr)
    if (route.path && route.path !== '*unmatchedRoute') {
        for (const locale of localePrefixes) {
            if (route.path === '/') {
                localeAwareRoutes.push(
                    {
                        ...route, path: `/${locale}`,
                    },
                    {
                        ...route, path: `/${locale}/`,
                    },
                )
            } else {
                localeAwareRoutes.push({
                    ...route,
                    path: `/${locale}${route.path}`,
                })
            }
        }
    } else if (route.path === '*unmatchedRoute') {
        for (const locale of localePrefixes) {
            localeAwareRoutes.push({
                ...route,
                path: `/${locale}/*unmatchedRoute`,
            })
        }
    }
}

const wrappedRoutes = localeAwareRoutes.map((route) => {
    const {
        showHeader = true,
        showHeaderMenu = true,
        showMobileMenu = true,
        showFooter = true,
        showSidebar = false,
        showUserHeader = false,
        requiredMode = null,
        showFullWidthHeader = false,
        partnerScoped = false,
        lockCurrencySelection: routeLockCurrencySelection = false,
        wizardHelpSection,
        props = {},
        ...rest
    } = route

    // Create a wrapper component that applies the template settings
    const TemplateControlledComponent = () => {
        const templateContext = usePublicTemplate()
        const location = useLocation()
        const intl = useIntl()
        const {
            clearPartnerConfig,
        } = usePartner()

        // Track page visit
        if (window.rdt) {
            window.rdt('track', 'PageVisit', {
                path: location.pathname,
                search: location.search,
            })
        }

        createEffect(() => {
            // Find matching child route if exists
            let activeRoute = route
            if (route.children && route.children.length > 0) {
                const matchedChild = route.children.find(child => {
                    if (!child.path) return false
                    const childPath = child.path.startsWith('/') ? child.path : `/${child.path}`
                    const fullChildPath = route.path + childPath
                    return location.pathname === fullChildPath
                })
                if (matchedChild && (matchedChild.title || matchedChild.description || matchedChild.titleMessage || matchedChild.descriptionMessage)) {
                    activeRoute = matchedChild
                }
            }

            const documentTitle = activeRoute.titleMessage
                ? intl.formatMessage(activeRoute.titleMessage)
                : (activeRoute.title ?? intl.formatMessage(sharedMessages.defaultDocumentTitle))
            const documentDescription = activeRoute.descriptionMessage
                ? intl.formatMessage(activeRoute.descriptionMessage)
                : (activeRoute.description ?? intl.formatMessage(sharedMessages.defaultDocumentDescription))

            setPageMetadata({
                description: documentDescription,
                noIndex: activeRoute.noIndex ?? false,
                title: documentTitle,
            })

            document.documentElement.lang = getLocaleFromPathname(location.pathname)

            // Note: web.base is currently the lodgr.co variant. This is a workaround to ensure the canonical link is correct.
            // TODO: Update this when the env var for web.base is updated to be lodgr.ca
            const baseUrl = app.env === 'production' ?  'https://lodgr.ca' : web.base
            const fullUrl = baseUrl + location.pathname
            setCanonicalLink(fullUrl)

            if (!partnerScoped) {
                clearPartnerConfig()
            }

            if (templateContext.isContextReady && templateContext.isContextReady()) {
                templateContext.setShowHeader(showHeader)
                templateContext.setShowHeaderMenu(showHeaderMenu)
                templateContext.setShowMobileMenu(showMobileMenu)
                templateContext.setShowPartnerHeader(partnerScoped)
                templateContext.setShowFooter(showFooter)
                templateContext.setShowSidebar(showSidebar)
                templateContext.setShowUserHeader(showUserHeader)
                templateContext.setShowFullWidthHeader(showFullWidthHeader)
                templateContext.setWizardHelpSection(wizardHelpSection)
                templateContext.setLockCurrencySelection(
                    activeRoute.lockCurrencySelection ?? routeLockCurrencySelection,
                )
            }
        })

        const Component = route.component
        return <Show when={partnerScoped} fallback={<Component {...props} />}><PartnerRouteWrapper>
            <Component {...props} />
        </PartnerRouteWrapper></Show>
    }

    return {
        ...rest,
        component: TemplateControlledComponent,
    }
})

const Routes = useRoutes(wrappedRoutes)
export default Routes
